'use client';

/**
 * useSetupAction — shared hook for payment method setup
 *
 * Routes add-button clicks: if the kind has `performSetup` in the registry,
 * calls `onPerformSetup` and manages loading/error state. Otherwise falls
 * through to `onAddClick` for the normal add flow.
 *
 * Used by both PurchaseDetailsScreen and PaymentMethodsListScreen so they
 * share the same state management without duplicating code.
 */

import { useState, useCallback } from 'react';
import { getKindConfig } from '../config/gatewayRegistry';
import { SetupCancelledError } from '../setup';

export interface SetupErrorInfo {
  title?: string;
  message?: string;
}

interface UseSetupActionReturn {
  /** Route a click: setup-kinds → onPerformSetup, others → onAddClick */
  handleClick: (kind: string) => void;
  /** Whether a setup flow is currently in progress */
  inProgress: boolean;
  /** Error info from the last failed setup attempt */
  error: SetupErrorInfo | undefined;
  /** Clear the error state (e.g. when user dismisses the popup) */
  resetError: () => void;
}

export const useSetupAction = (
  onPerformSetup: (kind: string) => Promise<void>,
  onAddClick: (kind: string) => void
): UseSetupActionReturn => {
  const [inProgress, setInProgress] = useState(false);
  const [error, setError] = useState<SetupErrorInfo>();

  const resetError = useCallback(() => setError(undefined), []);

  const handleClick = useCallback((kind: string) => {
    const config = getKindConfig(kind);
    if (!config?.performSetup) {
      onAddClick(kind);
      return;
    }

    setInProgress(true);
    setError(undefined);
    onPerformSetup(kind)
      .catch((e: unknown) => {
        if (!(e instanceof SetupCancelledError)) {
          const msg = e instanceof Error && 'errorMessage' in e
            ? (e as Error & { errorMessage?: string }).errorMessage
            : undefined;
          const title = e instanceof Error && 'errorTitle' in e
            ? (e as Error & { errorTitle?: string }).errorTitle
            : undefined;
          if (msg || title) {
            setError({ title, message: msg });
          }
        }
      })
      .finally(() => setInProgress(false));
  }, [onPerformSetup, onAddClick]);

  return {
    handleClick, inProgress, error, resetError
  };
};
